import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Stack,
  Slide,
  TextField,
  Button,
  Divider
} from '@mui/material';
import {
  IconArrowLeft,
  IconArrowRight,
  IconPresentation,
  IconEdit,
  IconCheck,
  IconBold,
  IconItalic,
  IconX,  
  IconUnderline,
  IconList,
  IconListNumbers,
  IconUpload
} from '@tabler/icons-react';
import { Editor, EditorState, RichUtils, ContentState, convertFromHTML } from 'draft-js';
import 'draft-js/dist/Draft.css';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * Component to display a simplified Google Slides-like presentation preview
 * with rich text editing capabilities
 *
 * @param {Object} props
 * @param {Object} props.lessonData - The lesson data to render in the presentation
 */
const SimpleGoogleSlidesPreview = ({ lessonData, generateGoogleSlides, isCreating }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideDirection, setSlideDirection] = useState('left');
  const [slideIn, setSlideIn] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [speakerNotesExpanded, setSpeakerNotesExpanded] = useState(false);

  // Rich text editor state
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  if (!lessonData) {
    return (
      <Paper
        elevation={1}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 3,
          bgcolor: '#f8f9fa'
        }}
      >
        <Typography variant="h6" sx={{ color: '#5f6368' }}>No lesson data available</Typography>
      </Paper>
    );
  }

  // Use the slides array from lessonData
  const slides = lessonData.slides || [];
  const totalSlides = slides.length > 0 ? slides.length : 1; // At least show title slide

  const handleNextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      setSlideDirection('left');
      setSlideIn(false);
      setEditMode(false);
      setTimeout(() => {
        setCurrentSlide(prev => prev + 1);
        setSlideIn(true);
      }, 300);
    }
  };

  const handlePrevSlide = () => {
    if (currentSlide > 0) {
      setSlideDirection('right');
      setSlideIn(false);
      setEditMode(false);
      setTimeout(() => {
        setCurrentSlide(prev => prev - 1);
        setSlideIn(true);
      }, 300);
    }
  };

  const toggleEditMode = () => {
    if (!editMode) {
      // Enter edit mode - initialize with current content
      const currentSlideData = slides[currentSlide];
      setEditedTitle(currentSlideData.title);

      // Convert plain text to rich text for the editor
      const blocksFromHTML = convertFromHTML(currentSlideData.content.replace(/\n/g, '<br/>'));
      const contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
      setEditorState(EditorState.createWithContent(contentState));
    } else {
      // Save changes logic would go here
      // For now, we're just toggling the mode
    }
    setEditMode(!editMode);
  };

  // Rich text editor handlers
  const handleEditorChange = (state) => {
    setEditorState(state);
  };

  const handleKeyCommand = (command) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      handleEditorChange(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const handleFormatting = (style) => {
    handleEditorChange(RichUtils.toggleInlineStyle(editorState, style));
  };

  const handleBlockType = (blockType) => {
    handleEditorChange(RichUtils.toggleBlockType(editorState, blockType));
  };

  // Render the current slide
  const renderSlide = () => {
    if (slides.length === 0) {
      // Fallback title slide if no slides are available
      return (
        <Box sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
          bgcolor: 'white',
          backgroundImage: 'linear-gradient(to bottom, #f6f6f6 0%, white 100%)'
        }}>
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              mb: 3,
              color: '#4285f4' // Google Blue
            }}
          >
            {`${lessonData.metadata.classTitle}: ${lessonData.metadata.title}`}
          </Typography>

          <Typography
            variant="h6"
            align="center"
            sx={{
              color: '#5f6368'
            }}
          >
            {`${lessonData.metadata.module} - Lesson ${lessonData.metadata.lesson}`}
          </Typography>
        </Box>
      );
    }

    // Display slide from the slides array
    const slide = slides[currentSlide];

    // Edit mode
    if (editMode) {
      return (
        <Box sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          p: 3,
          bgcolor: 'white',
          backgroundImage: 'linear-gradient(to bottom, #f6f6f6 0%, white 100%)'
        }}>
          <TextField
            fullWidth
            label="Slide Title"
            variant="outlined"
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {/* Rich Text Editor Toolbar */}
            <Box sx={{
              display: 'flex',
              gap: 1,
              mb: 1,
              p: 1,
              borderBottom: '1px solid #e0e0e0',
              bgcolor: '#f5f5f5',
              borderRadius: '4px 4px 0 0'
            }}>
              <IconButton
                size="small"
                onClick={() => handleFormatting('BOLD')}
                color={editorState.getCurrentInlineStyle().has('BOLD') ? 'primary' : 'default'}
              >
                <IconBold size={18} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleFormatting('ITALIC')}
                color={editorState.getCurrentInlineStyle().has('ITALIC') ? 'primary' : 'default'}
              >
                <IconItalic size={18} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleFormatting('UNDERLINE')}
                color={editorState.getCurrentInlineStyle().has('UNDERLINE') ? 'primary' : 'default'}
              >
                <IconUnderline size={18} />
              </IconButton>
              <Box sx={{ borderLeft: '1px solid #e0e0e0', mx: 1 }} />
              <IconButton
                size="small"
                onClick={() => handleBlockType('unordered-list-item')}
              >
                <IconList size={18} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleBlockType('ordered-list-item')}
              >
                <IconListNumbers size={18} />
              </IconButton>
            </Box>

            {/* Rich Text Editor */}
            <Box sx={{
              flex: 1,
              border: '1px solid #e0e0e0',
              borderRadius: '0 0 4px 4px',
              p: 2,
              mb: 3,
              overflow: 'auto',
              bgcolor: '#ffffff'
            }}>
              <Editor
                editorState={editorState}
                onChange={handleEditorChange}
                handleKeyCommand={handleKeyCommand}
                placeholder="Enter slide content..."
              />
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={toggleEditMode}
              startIcon={<IconCheck size={18} />}
              sx={{ 
                alignSelf: 'flex-end',
                mb: 5  // Add margin bottom to move it up
              }}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      );
    }

    // View mode
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 4,
        bgcolor: 'white',
        backgroundImage: 'linear-gradient(to bottom, #f6f6f6 0%, white 100%)'
      }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            mb: 3,
            color: '#4285f4' // Google Blue
          }}
        >
          {slide.title}
        </Typography>

        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ flex: 1 }}>
            {slide.content.split('\n').map((line, i) => (
              <Typography
                key={i}
                variant="body1"
                paragraph
                sx={{
                  color: '#3c4043',
                  fontSize: '18px',
                  mb: 2
                }}
              >
                {line}
              </Typography>
            ))}
          </Box>

          {slide.image && (
            <Box sx={{
              flex: 1,
              ml: 3,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              bgcolor: '#ffffff',
              borderRadius: 1,
              border: '1px dashed #dadce0',
              overflow: 'hidden'
            }}>
              <Box sx={{
                width: '100%',
                height: '200px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
                background: 'repeating-linear-gradient(45deg, #f1f3f4, #f1f3f4 10px, #e8eaed 10px, #e8eaed 20px)'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {slide.image}
                </Typography>
              </Box>
              
              <Button 
                variant="outlined" 
                size="small"
                startIcon={<IconUpload size={16} />}
                sx={{ 
                  color: '#4285f4',
                  borderColor: '#4285f4',
                  mb: 2,
                  '&:hover': {
                    borderColor: '#1a73e8',
                    bgcolor: 'rgba(66, 133, 244, 0.04)'
                  }
                }}
              >
                Upload Image
              </Button>
            </Box>
          )}
        </Box>
        <Divider />

        {slide.speakerNotes && (
          <>
            {/* Collapsed speaker notes button */}
            <Box 
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '36px',
                backgroundColor: '#f1f3f4',
                borderTop: '1px solid #dadce0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10
              }}
              onClick={() => setSpeakerNotesExpanded(true)}
            >
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#5f6368', mr: 1 }}>
                SPEAKER NOTES
              </Typography>
              <ExpandLessIcon fontSize="small" sx={{ color: '#5f6368' }} />
            </Box>
            
            {/* Expanded speaker notes overlay with animation */}
            <Box 
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255,255,255,0.98)',
                zIndex: 20,
                display: 'flex',
                flexDirection: 'column',
                p: 2,
                boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                transform: speakerNotesExpanded ? 'translateY(0)' : 'translateY(100%)',
                opacity: speakerNotesExpanded ? 1 : 0,
                transition: 'transform 0.3s ease, opacity 0.2s ease',
                pointerEvents: speakerNotesExpanded ? 'auto' : 'none'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#5f6368' }}>
                  SPEAKER NOTES
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => setSpeakerNotesExpanded(false)}
                  sx={{ color: '#5f6368' }}
                >
                  <IconX size={18} />
                </IconButton>
              </Box>
              
              <Box sx={{
                flex: 1,
                overflow: 'auto',
                bgcolor: '#f1f3f4',
                borderRadius: 1,
                borderLeft: '4px solid #4285f4',
                p: 2,
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#bdc1c6',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f3f4',
                }
              }}>
                {/* Objective */}
                {slide.speakerNotes.objective && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ color: '#3c4043', fontWeight: 'bold' }}>
                      Objective:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#3c4043' }}>
                      {slide.speakerNotes.objective}
                    </Typography>
                  </Box>
                )}

                {/* Questions */}
                {slide.speakerNotes.questions && slide.speakerNotes.questions.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ color: '#3c4043', fontWeight: 'bold' }}>
                      Questions:
                    </Typography>
                    <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                      {slide.speakerNotes.questions.map((question, idx) => (
                        <li key={idx}>
                          <Typography variant="body2" sx={{ color: '#3c4043' }}>
                            {question}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}

                {/* Image Prompt */}
                {slide.speakerNotes.imagePrompt && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ color: '#3c4043', fontWeight: 'bold' }}>
                      Image Prompt:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#3c4043', fontStyle: 'italic' }}>
                      {slide.speakerNotes.imagePrompt}
                    </Typography>
                  </Box>
                )}

                {/* AI Prompt */}
                {slide.speakerNotes.aiPrompt && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ color: '#3c4043', fontWeight: 'bold' }}>
                      AI Prompt:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#3c4043', fontStyle: 'italic' }}>
                      {slide.speakerNotes.aiPrompt}
                    </Typography>
                  </Box>
                )}

                {/* Scaffolding Tips */}
                {slide.speakerNotes.scaffoldingTips && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ color: '#3c4043', fontWeight: 'bold' }}>
                      Scaffolding Tips:
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      {slide.speakerNotes.scaffoldingTips.support && (
                        <Typography variant="body2" sx={{ color: '#3c4043' }}>
                          <strong>Support:</strong> {slide.speakerNotes.scaffoldingTips.support}
                        </Typography>
                      )}
                      {slide.speakerNotes.scaffoldingTips.core && (
                        <Typography variant="body2" sx={{ color: '#3c4043' }}>
                          <strong>Core:</strong> {slide.speakerNotes.scaffoldingTips.core}
                        </Typography>
                      )}
                      {slide.speakerNotes.scaffoldingTips.challenge && (
                        <Typography variant="body2" sx={{ color: '#3c4043' }}>
                          <strong>Challenge:</strong> {slide.speakerNotes.scaffoldingTips.challenge}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Teacher Tip */}
                {slide.speakerNotes.teacherTip && (
                  <Box sx={{
                    p: 1,
                    mb: 1,
                    bgcolor: '#e8f0fe',
                    borderRadius: 1,
                    borderLeft: '3px solid #1a73e8'
                  }}>
                    <Typography variant="body2" sx={{ color: '#1a73e8', fontWeight: 'bold' }}>
                      Teacher Tip:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#3c4043' }}>
                      {slide.speakerNotes.teacherTip}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    );
  };

  return (
    <Paper
      elevation={1}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        bgcolor: '#f1f3f4', // Google Slides background
        border: '1px solid #dadce0',
        borderRadius: 1
      }}
    >
      {/* Slide content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          m: 2,
          border: '1px solid #dadce0',
          borderRadius: 1,
          bgcolor: 'white',
          boxShadow: '0 1px 3px 1px rgba(60,64,67,.15)'
        }}
      >
        <Slide direction={slideDirection} in={slideIn} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0
            }}
          >
            {renderSlide()}
          </Box>
        </Slide>
      </Box>

      {/* Navigation controls */}
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
        sx={{
          p: 1,
          borderTop: '1px solid #dadce0',
          bgcolor: '#f1f3f4' // Google Slides toolbar color
        }}
      >
        <Box>
          <IconButton
            onClick={handlePrevSlide}
            disabled={currentSlide === 0}
            size="small"
            sx={{ color: '#5f6368' }}
          >
            <IconArrowLeft />
          </IconButton>
          <IconButton
            onClick={handleNextSlide}
            disabled={currentSlide === totalSlides - 1}
            size="small"
            sx={{ color: '#5f6368' }}
          >
            <IconArrowRight />
          </IconButton>
        </Box>

        <Typography variant="body2" sx={{ color: '#5f6368' }}>
          Slide {currentSlide + 1} of {totalSlides}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={toggleEditMode}
            size="small"
            sx={{ color: editMode ? '#4285f4' : '#5f6368', mr: 1 }}
          >
            <IconEdit size={16} />
          </IconButton>

          <Button
            onClick={() => {
              generateGoogleSlides()
            }}
            size="small"
            disabled={isCreating}
            className="rainbow-button" 
            sx={{ color: '#ffffff', mr: 1 }}
          >
            <IconPresentation size={16} />
            {isCreating ? "Generating..." : "Generate Google Slides"}
          </Button>

          {/* <Typography variant="caption" sx={{ color: '#5f6368' }}>
            Preview
          </Typography> */}

        </Box>
      </Stack>
    </Paper>
  );
};

export default SimpleGoogleSlidesPreview;
